<template>
	<div>
		<CRow class="align-items-start">
			<CCol class="mb-3">
				<div class="typo-body-2 color-black-45">
					Export จะส่งไฟล์ .xlsx เข้าอีเมลของคุณ แก้ในไฟล์แล้ว Import กลับได้
					· Import อ้างอิงตาม <strong>Customer ID</strong> และไม่ลบข้อมูลที่ไม่อยู่ในไฟล์
					— ถ้าต้องการซ่อนจากหน้า Ecom ให้ตั้ง <strong>status = 0</strong>
				</div>
			</CCol>
			<!-- ปุ่มทั้งหมดอยู่แถวเดียวเสมอ — ข้อความด้านซ้ายเป็นฝ่ายตัดบรรทัดแทน -->
			<!-- BE มี permission แค่ view / manage → Export / Import / Create ผูกกับ manage (hasEditPermission) -->
			<CCol md="auto" class="action-buttons d-flex flex-nowrap justify-content-end">
				<CButton
					v-if="hasEditPermission"
					:disabled="isExporting || isLoading"
					class="btn-export mb-3"
					color="tertiary"
					data-test-id="export-button"
					@click="openExportConfirm"
				>
					{{ isExporting ? 'Exporting' : 'Export' }}
				</CButton>
				<CButton
					v-if="hasEditPermission"
					:disabled="isImporting"
					class="btn-import mb-3 ml-3"
					color="secondary"
					data-test-id="import-button"
					@click="openImportCondition"
				>
					{{ isImporting ? 'Importing' : 'Import' }}
				</CButton>
				<CButton
					v-if="hasEditPermission"
					class="btn-create mb-3 ml-3"
					color="secondary"
					data-test-id="create-button"
					@click="openCreateDrawer"
				>
					<CIcon class="mr-2" name="cil-plus" />
					Create university
				</CButton>
			</CCol>
		</CRow>

		<CRow>
			<CCol class="d-flex align-items-start">
				<form
					class="search-form flex-fill"
					@submit.prevent="handleSearch"
				>
					<CInput
						v-model.trim="queryParams.q"
						data-test-id="search-input"
						placeholder="Search by Customer ID, Company name (TH / EN)"
					>
						<template #prepend-content>
							<CIcon
								name="cil-magnifying-glass"
								data-test-id="search-button"
							/>
						</template>
					</CInput>
				</form>
				<BaseFilterButton
					class="ml-3"
					data-test-id="filter-button"
					:is-open="isShowFilter"
					:has-filter="hasFilter"
					@onClick="toggleFilter"
				/>
			</CCol>
		</CRow>

		<CRow>
			<CCol>
				<transition name="fade">
					<div
						v-if="isShowFilter"
						class="filter-box rounded-sm pt-3 px-3 mb-3"
						data-test-id="filter-box"
					>
						<CRow>
							<CCol md="3">
								<BaseDropDown
									v-model="filter.status"
									:searchable="false"
									:options="UNIDAY_UNIVERSITY_STATUS_OPTIONS"
									:allow-empty="false"
									class="select-custom"
									label="name"
									track-by="value"
									label-drop-down="Status"
									@input="handleStatusChange"
								/>
							</CCol>
						</CRow>
					</div>
				</transition>
			</CCol>
		</CRow>

		<CRow class="mb-4">
			<CCol>
				<BaseSearchFormFooter
					:count="list.meta.total"
					append-text="university(ies) found"
					@onReset="resetFilter"
				/>
			</CCol>
		</CRow>

		<CRow>
			<CCol>
				<BaseTable
					:is-loading="isLoading"
					:pagination="{
						pages: list.meta.lastPage,
						activePage: list.meta.currentPage,
					}"
					:fields="tableFields"
					:items="tableItems"
					:clickable-rows="hasEditPermission"
					striped
					vertical-align="top"
					class="table-custom table-uniday-university"
					@onPaginationClick="handlePageChange"
					@onRowClick="openEditDrawer"
				>
					<template #editId="{item}">
						<CLink
							class="link-edit"
							data-test-id="edit-link"
							@click.stop="openEditDrawer(item)"
						>
							Edit
						</CLink>
					</template>
					<template #no-items-view>
						<div class="empty-table-element">
							<p class="subtitle">
								{{ $t('global.searchNotFound', { field: 'universities' }) }}
							</p>
						</div>
					</template>
					<template #status="{item}">
						<CBadge class="badge-status" :color="UNIDAY_UNIVERSITY_COLOR.STATUS[item]">
							{{ UNIDAY_UNIVERSITY_TITLE.STATUS[item] }}
						</CBadge>
					</template>
				</BaseTable>
			</CCol>
		</CRow>

		<!-- CREATE / EDIT DRAWER (ไม่มีลบ — ปิดด้วย status แทน) -->
		<DrawerUnidayUniversity
			ref="drawer-uniday-university"
			:is-submitting="isSubmitting"
			@onSubmit="handleSubmitUniversity"
		/>

		<!-- IMPORT CONDITION MODAL (เปิดก่อนเลือกไฟล์) -->
		<CModal
			centered
			class="modal-dialog-scrollable modal-import-condition"
			:show.sync="modalImportCondition.isShowing"
		>
			<template #header-wrapper>
				<ModalHeaderWrapper
					title="Import universities"
					@onClose="closeImportCondition"
				/>
			</template>
			<template #default>
				<div class="import-condition" data-test-id="import-condition">
					<ul class="condition-list">
						<li>ไฟล์ <strong>.xlsx / .xls</strong> — แนะนำให้ใช้ไฟล์จาก <strong>Export</strong> มาแก้ (header ตรงกันแน่นอน)</li>
						<li><code>customer_id</code> มีอยู่แล้ว = <strong>อัปเดต</strong> · ยังไม่มี = <strong>เพิ่มใหม่</strong></li>
						<li>ข้อมูลที่ไม่อยู่ในไฟล์ <strong>ไม่ถูกลบ</strong> — ถ้าจะซ่อนให้ตั้ง <code>status = 0</code></li>
						<li>
							<code>customer_id</code> จำเป็น (ตัวเลข) · <code>status</code> ใส่ 1 (แสดง) / 0 (ซ่อน)
							· <code>organize_id</code> ไม่เกิน 100 ตัวอักษร
						</li>
						<li class="text-alert">
							มีแถวไหนไม่ผ่าน (เช่น <code>customer_id</code> ซ้ำในไฟล์) = <strong>ทั้งไฟล์ไม่ถูกบันทึก</strong>
							ระบบแจ้งเลขแถว + เหตุผลให้แก้
						</li>
					</ul>
					<div class="typo-caption-2 color-black-45">
						ไฟล์ผ่านแล้วจะเข้าคิวประมวลผล ดูผลได้ที่ <strong>Import History</strong>
					</div>
				</div>
			</template>
			<template #footer>
				<CButton
					class="transparent"
					color="tertiary"
					@click="closeImportCondition"
				>
					Cancel
				</CButton>
				<CButton
					class="ml-3"
					color="secondary"
					data-test-id="choose-file-button"
					@click="handleChooseImportFile"
				>
					Choose file
				</CButton>
			</template>
		</CModal>

		<!-- IMPORTING MODAL -->
		<CModal
			centered
			:show="isImporting"
			:close-on-backdrop="false"
		>
			<template #header-wrapper>
				<ModalHeaderWrapper
					title="Importing"
					@onClose="cancelImport"
				/>
			</template>
			<template #default>
				<div class="py-5">
					<BaseLoading is-full-page />
				</div>
			</template>
			<template #footer>
				<CButton
					class="transparent mx-auto"
					color="tertiary"
					@click="cancelImport"
				>
					Cancel
				</CButton>
			</template>
		</CModal>

		<!-- IMPORT QUEUED MODAL (204 — BE รับไฟล์แล้ว ประมวลผลใน queue) -->
		<CModal
			centered
			:show.sync="modalImportQueued.isShowing"
		>
			<template #header-wrapper>
				<ModalHeaderWrapper
					title="Import in progress"
					@onClose="closeImportQueued"
				/>
			</template>
			<template #default>
				<div class="typo-body-2" data-test-id="import-queued">
					ระบบได้รับไฟล์แล้ว กำลังประมวลผลในคิว
					ข้อมูลในหน้านี้จะอัปเดตเมื่อประมวลผลเสร็จ — ดูผลได้ที่ <strong>Import History</strong>
				</div>
			</template>
			<template #footer>
				<CButton
					class="transparent"
					color="tertiary"
					@click="closeImportQueued"
				>
					Okay
				</CButton>
				<CButton
					class="ml-3"
					color="secondary"
					data-test-id="go-import-history"
					@click="goToImportHistory"
				>
					Go to Import History
				</CButton>
			</template>
		</CModal>

		<!-- IMPORT FILE ERROR MODAL (ทั้งไฟล์ถูก reject เช่น header ไม่ตรง) -->
		<CModal
			centered
			class="modal-dialog-scrollable"
			:show.sync="modalImportError.isShowing"
		>
			<template #header-wrapper>
				<ModalHeaderWrapper
					title="Unable to import"
					@onClose="closeImportError"
				/>
			</template>
			<template #default>
				<div class="mb-3 mt-1">
					ไฟล์ไม่ผ่านการตรวจสอบ <strong>ยังไม่มีข้อมูลใดถูกบันทึก</strong> แก้ตามรายการด้านล่างแล้ว Import ใหม่
				</div>
				<div
					v-for="(error, index) in modalImportError.errors"
					:key="index"
					class="mb-2 text-alert"
				>
					{{ error }}
				</div>
			</template>
			<template #footer>
				<CButton
					class="transparent"
					color="tertiary"
					@click="closeImportError"
				>
					Okay
				</CButton>
			</template>
		</CModal>

		<!-- EXPORT CONFIRM (BE ส่งไฟล์เข้าอีเมลคนกด) -->
		<BaseModalConfirm
			ref="modal-export"
			:is-submitting="isExporting"
			:close-after-confirm="false"
			title="Export universities"
			description="ระบบจะส่งไฟล์ .xlsx ตาม search / filter ที่เลือกอยู่ เข้าอีเมลของคุณ อาจใช้เวลาสักครู่"
			primary-button-text="Send"
			primary-button-loading-text="Sending"
			@onConfirm="handleExport"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { CancelToken, isCancel } from 'axios';
import ModalHeaderWrapper from '@/components/ModalHeaderWrapper.vue';
import BaseModalConfirm from '@/components/BaseModalConfirm.vue';
import DrawerUnidayUniversity from '@/components/DrawerUnidayUniversity.vue';
import isHavePermission from '../mixins/isHavePermission';
import { ROUTE_NAME } from '../enums/route';
import {
	UNIDAY_UNIVERSITY_STATUSES,
	UNIDAY_UNIVERSITY_STATUS_OPTIONS,
	UNIDAY_UNIVERSITY_TITLE,
	UNIDAY_UNIVERSITY_COLOR,
	UNIDAY_UNIVERSITY_TABLE_FIELDS,
	UNIDAY_UNIVERSITY_ACTION_FIELD,
	UNIDAY_UNIVERSITY_IMPORT_ACCEPT,
	UNIDAY_UNIVERSITY_IMPORT_FILE_EXTENSIONS,
} from '../enums/unidayUniversities';
import { exportUnidayUniversityAPI } from '../services/api/unidayUniversities.api';
import { transformedUnidayUniversityImportFileErrors } from '../assets/js/transform/unidayUniversities';
import {
	cleanObject,
	cloneDeep,
	hasSearchFilter,
	getBooleanQueryParameter,
	pathOr,
} from '../assets/js/helpers';

const getSelectedOption = (value = null) => {
	return UNIDAY_UNIVERSITY_STATUS_OPTIONS.find((option) => option.value === value)
		|| UNIDAY_UNIVERSITY_STATUS_OPTIONS[0];
};

export default {
	name: 'UnidayUniversities',

	components: {
		ModalHeaderWrapper,
		BaseModalConfirm,
		DrawerUnidayUniversity,
	},

	mixins: [isHavePermission],

	data() {
		const status = getBooleanQueryParameter(this.$route.query.status);

		return {
			UNIDAY_UNIVERSITY_STATUSES,
			UNIDAY_UNIVERSITY_STATUS_OPTIONS,
			UNIDAY_UNIVERSITY_TITLE,
			UNIDAY_UNIVERSITY_COLOR,
			UNIDAY_UNIVERSITY_TABLE_FIELDS,
			queryParams: {
				q: this.$route.query.q || null,
				page: Number(this.$route.query.page) || null,
				status,
			},
			filter: {
				status: getSelectedOption(status),
			},
			isShowFilter: false,
			isExporting: false,
			importCancelSource: null,
			modalImportCondition: {
				isShowing: false,
			},
			modalImportQueued: {
				isShowing: false,
			},
			modalImportError: {
				isShowing: false,
				errors: [],
			},
		};
	},

	computed: {
		...mapState('unidayUniversities', {
			list: 'list',
			isImporting: (state) => state.import.isImporting,
			isSubmitting: (state) => state.form.isSubmitting,
		}),

		...mapGetters({
			getUnidayUniversityFormById: 'unidayUniversities/getUnidayUniversityFormById',
		}),

		isLoading() {
			return this.list.isLoading;
		},

		hasFilter() {
			return hasSearchFilter(this.queryParams);
		},

		// BaseTable อ่าน `rowClass` ต่อแถว (และใช้ `key` เป็น v-for key)
		// แถว inactive (ซ่อนจากหน้า Ecom) ทำตัวหนังสือจางให้แยกออกจากแถวที่ active ได้ทันที
		tableFields() {
			return this.hasEditPermission
				? [...UNIDAY_UNIVERSITY_TABLE_FIELDS, UNIDAY_UNIVERSITY_ACTION_FIELD]
				: UNIDAY_UNIVERSITY_TABLE_FIELDS;
		},

		tableItems() {
			return this.list.data.map((item) => ({
				...item,
				key: item.id || item.customerId,
				// slot ของ BaseTable ได้ "ค่าของ cell" ไม่ใช่ทั้งแถว → ใส่ id ไว้ใน cell ของปุ่ม Edit
				editId: item.id,
				rowClass: item.status === UNIDAY_UNIVERSITY_STATUSES.INACTIVE ? 'row-inactive' : '',
			}));
		},

		// export ตามเงื่อนไข search / filter ที่เลือกอยู่ (ไม่ส่ง page — เอาทุกหน้า)
		exportParams() {
			return cleanObject({
				q: this.queryParams.q,
				status: this.queryParams.status,
			});
		},
	},

	async created() {
		this.isShowFilter = this.hasFilter;
		await this.getUnidayUniversities(cleanObject(cloneDeep(this.queryParams)));
	},

	methods: {
		...mapActions({
			getUnidayUniversities: 'unidayUniversities/getUnidayUniversities',
			importUnidayUniversities: 'unidayUniversities/importUnidayUniversities',
			createUnidayUniversity: 'unidayUniversities/createUnidayUniversity',
			updateUnidayUniversity: 'unidayUniversities/updateUnidayUniversity',
			showToast: 'toast/showToast',
		}),

		toggleFilter() {
			this.isShowFilter = !this.isShowFilter;
		},

		updateUrlParams() {
			const query = cleanObject(cloneDeep(this.queryParams));
			this.$router.push({ query }).catch(() => {});
		},

		handlePageChange(page) {
			this.queryParams.page = page;
			this.updateUrlParams();
		},

		handleSearch() {
			this.queryParams.page = null;
			this.updateUrlParams();
		},

		handleStatusChange({ value }) {
			this.queryParams.page = null;
			this.queryParams.status = value;
			this.updateUrlParams();
		},

		resetFilter() {
			this.queryParams = {
				q: null,
				page: null,
				status: null,
			};
			this.updateUrlParams();
		},

		openCreateDrawer() {
			this.$refs['drawer-uniday-university'].open();
		},

		openEditDrawer(id) {
			if (!this.hasEditPermission) {
				return;
			}

			const formData = this.getUnidayUniversityFormById(id);
			if (formData) {
				this.$refs['drawer-uniday-university'].open(formData);
			}
		},

		async handleSubmitUniversity({ id, params, isEditMode }) {
			const drawer = this.$refs['drawer-uniday-university'];

			try {
				if (isEditMode) {
					await this.updateUnidayUniversity({ id, params });
				} else {
					await this.createUnidayUniversity(params);
				}

				drawer.close();
				await this.getUnidayUniversities(cleanObject(cloneDeep(this.queryParams)));
			} catch (error) {
				// 422 (เช่น customer_id ซ้ำ) → โชว์ใต้ช่องใน drawer · toast ขึ้นจาก interceptor แล้ว
				drawer.setServerErrors(error);

				if (!error._toastShown) {
					this.showToast({
						header: this.$t(isEditMode ? 'global.errorMessageTitleEdit' : 'global.errorMessageTitleCreate'),
						content: pathOr(this.$t('global.errorMessage'), ['response', 'data', 'message'])(error),
						type: 'danger',
					});
				}
			}
		},

		openExportConfirm() {
			this.$refs['modal-export'].open();
		},

		// 200 ไม่มี body — BE สร้างไฟล์แล้วส่งเข้าอีเมลคนกด (sendgrid template uniday_university_export)
		async handleExport() {
			this.isExporting = true;

			try {
				await exportUnidayUniversityAPI(this.exportParams);

				this.showToast({
					header: this.$t('global.successMessageTitle'),
					content: 'ระบบกำลังสร้างไฟล์ จะส่งเข้าอีเมลของคุณภายในไม่กี่นาที',
					type: 'success',
				});
			} catch (e) {
				if (!e._toastShown) {
					this.showToast({
						header: this.$t('global.errorMessageExport'),
						content: this.$t('global.errorMessage'),
						type: 'danger',
					});
				}
			} finally {
				this.isExporting = false;
				this.$refs['modal-export'].close();
			}
		},

		openImportCondition() {
			this.modalImportCondition.isShowing = true;
		},

		closeImportCondition() {
			this.modalImportCondition.isShowing = false;
		},

		handleChooseImportFile() {
			this.closeImportCondition();
			this.handleSelectImportFile();
		},

		handleSelectImportFile() {
			const fileSelector = document.createElement('input');
			fileSelector.setAttribute('type', 'file');
			fileSelector.setAttribute('accept', UNIDAY_UNIVERSITY_IMPORT_ACCEPT);
			fileSelector.onchange = () => {
				const file = fileSelector.files.item(0);
				if (file) {
					this.handleImport(file);
				}
			};
			fileSelector.click();
		},

		isValidImportFile(file) {
			const extension = (file.name || '').split('.').pop().toLowerCase();
			return UNIDAY_UNIVERSITY_IMPORT_FILE_EXTENSIONS.includes(extension);
		},

		async handleImport(file) {
			// กันไฟล์ผิดประเภทตั้งแต่ FE (BE ต้องกันซ้ำด้วย)
			if (!this.isValidImportFile(file)) {
				this.showToast({
					header: 'Unable to import',
					content: 'Only .xlsx / .xls file is supported.',
					type: 'danger',
				});
				return;
			}

			this.importCancelSource = CancelToken.source();

			try {
				await this.importUnidayUniversities({
					file,
					config: {
						headers: { 'Content-Type': 'multipart/form-data' },
						cancelToken: this.importCancelSource.token,
					},
				});

				// 204 — เข้า queue แล้ว ผลจริงดูที่ Import History
				this.modalImportQueued.isShowing = true;
			} catch (e) {
				if (isCancel(e)) {
					return;
				}

				const fileErrors = transformedUnidayUniversityImportFileErrors(e?.response?.data);
				if (fileErrors.length) {
					this.modalImportError.errors = fileErrors;
					this.modalImportError.isShowing = true;
					return;
				}

				if (!e._toastShown) {
					this.showToast({
						header: 'Unable to import',
						content: this.$t('global.errorMessage'),
						type: 'danger',
					});
				}
			} finally {
				this.importCancelSource = null;
			}
		},

		cancelImport() {
			if (this.importCancelSource) {
				this.importCancelSource.cancel('Operation canceled by the user');
			}
		},

		closeImportQueued() {
			this.modalImportQueued.isShowing = false;
		},

		goToImportHistory() {
			this.closeImportQueued();
			this.$router.push({ name: ROUTE_NAME.PRODUCT_IMPORT_HISTORY });
		},

		closeImportError() {
			this.modalImportError.isShowing = false;
		},
	},
};
</script>

<style lang="scss" scoped>
	.filter-box {
		background-color: $color-gray-100;
	}

	.badge-status {
		min-width: rem(65);
	}

	.condition-list {
		@include typo-body-2;

		padding-left: rem(20);

		li {
			margin-bottom: rem(8);
		}
	}

	.text-alert {
		color: $color-alert;
	}

	::v-deep .table-uniday-university {
		th,
		td {
			font-size: rem(14);
		}

		// คลิกแถวเปิด drawer edit → ให้ cursor บอกว่ากดได้
		.clickable tbody tr {
			cursor: pointer;
		}

		.link-edit {
			color: $color-hypertext;
			cursor: pointer;
			font-weight: 600;
		}

		// แถว status = 0 — ตัวหนังสือจางทั้งแถว (badge มีสีของตัวเอง ยังอ่านชัด)
		.row-inactive td {
			color: $color-black-45;
		}
	}
</style>
